import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DossierService } from '../../services/dossier.service';
import { AuthService } from '../../services/auth.service';
import { Document, Upload, UploadStatus } from '../../models/dossier.model';

@Component({
  selector: 'app-extraction-summary',
  templateUrl: './extraction-summary.component.html',
  styleUrls: ['./extraction-summary.component.css'],
})
export class ExtractionSummaryComponent implements OnInit, OnDestroy {

  dossierId = '';
  uploadId = '';

  documents: Document[] = [];
  upload: Upload | null = null;

  loading = false;
  error = '';

  private pollTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dossierService: DossierService,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.dossierId = this.route.snapshot.paramMap.get('id') || '';
    this.uploadId = this.route.snapshot.paramMap.get('uid') || '';
    if (this.dossierId && this.uploadId) {
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  loadData(): void {
      this.loading = true;
      this.error = '';
      this.dossierService.getDossier(this.dossierId).subscribe({
        next: (dossier) => {
          this.upload = dossier.uploads?.find(u => u.oid === this.uploadId) || null;
          if (this.isExtractionDone) {
            this.loadDocuments();
          } else if (this.isFailedStatus) {
            this.error = 'Processing failed: ' + (this.upload?.status || 'unknown');
            this.loading = false;
          } else {
            this.loading = false;
            this.startPolling();
          }
        },
        error: (err) => {
          this.error = err?.error?.detail || err?.message || 'Failed to load dossier';
          this.loading = false;
        },
      });
    }

  private loadDocuments(): void {
    this.dossierService.getDocuments(this.dossierId, this.uploadId).subscribe({
      next: (docs) => {
        this.documents = docs;
        this.loading = false;
        // Refresh balance — extraction deducted tokens server-side
        this.auth.refreshProfile();
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to load documents';
        this.loading = false;
      },
    });
  }

  /** Poll dossier status every 3s until extraction is done. */
  private startPolling(): void {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => {
        this.dossierService.getDossier(this.dossierId).subscribe({
          next: (dossier) => {
            this.upload = dossier.uploads?.find(u => u.oid === this.uploadId) || null;
            if (this.isExtractionDone) {
              this.stopPolling();
              this.loadDocuments();
            } else if (this.isFailedStatus) {
              this.stopPolling();
              this.error = 'Processing failed: ' + (this.upload?.status || 'unknown');
            }
          },
        });
      }, 3000);
    }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /** True when the upload has reached extraction_completed or completed status. */
  get isExtractionDone(): boolean {
      if (!this.upload) return false;
      return this.upload.status === UploadStatus.EXTRACTION_COMPLETED
        || this.upload.status === UploadStatus.COMPLETED;
    }

    get isFailedStatus(): boolean {
      if (!this.upload) return false;
      const failed: string[] = [
        UploadStatus.PROCESSING_FAILED,
        UploadStatus.OCR_FAILED,
        UploadStatus.CLASSIFICATION_FAILED,
        UploadStatus.EXTRACTION_FAILED,
      ];
      return failed.includes(this.upload.status);
    }

  openDetail(doc: Document): void {
    this.router.navigate([
      '/dossiers', this.dossierId,
      'uploads', this.uploadId,
      'documents', doc.oid,
      'extract',
    ]);
  }

  /** Get extraction billing tokens from the extraction_result JSONB. */
  getExtractionTokens(doc: Document): number | null {
    const bt = doc.extraction_result?.['billing_tokens'];
    if (bt && typeof bt === 'object' && 'extraction' in bt) {
      return (bt as any).extraction;
    }
    return null;
  }

  goBack(): void {
    this.router.navigate(['/dossiers', this.dossierId]);
  }
}
